import * as React from 'react'
import { closeProjectTagModal } from '../../store/modals/actions'
import ModalHeader from './Parts/ModalHeader'
import TooltipError from '../Tooltips/ErrorTooltip'
import Notification from '../../utilities/Notification'
import { ProjectTagsController } from '../../controllers'
import { AppState } from '../../store'
import { AnyAction, Dispatch } from 'redux'
import { connect } from 'react-redux'
import ModalWindow from './Parts/ModalWindow'
import ModalMiddle from './Parts/ModalMiddle'
import ModalLoader from './Parts/ModalLoader'
import ModalContent from './Parts/ModalContent'
import { withTranslation, WithTranslation } from 'react-i18next'
import { CurrentUser, ProjectTag } from '../../types'

interface IStateToProps {
  projectTag: ProjectTag
  onSubmit?: (projectTag: ProjectTag) => void
  currentUser: CurrentUser
}

interface IDispatchToProps {
  close: typeof closeProjectTagModal
}

type IProps = IDispatchToProps & IStateToProps & WithTranslation

export enum ProjectTagModalTab {
  DETAILS = 'details',
  RULES = 'rules',
}

interface IState {
  didInitialLoad: boolean
  activeTab: ProjectTagModalTab
  projectTag: ProjectTag | null
  errors: any
}

class ProjectTagModal extends React.Component<IProps, IState> {
  static defaultProps = {
    projectTag: {}
  }

  constructor(props: IProps) {
    super(props)

    this.state = {
      didInitialLoad: false,
      activeTab: ProjectTagModalTab.DETAILS,
      projectTag: null,
      errors: {},
    }

    this.fetchForm = this.fetchForm.bind(this)
    this.onNameChange = this.onNameChange.bind(this)
    this.onFormSubmit = this.onFormSubmit.bind(this)
    this.onProjectTagModalCloseClick = this.onProjectTagModalCloseClick.bind(this)
    this.onErrorsDismiss = this.onErrorsDismiss.bind(this)
  }

  componentDidMount() {
    this.fetchForm()
  }

  async fetchForm() {
    const { projectTag: propsProjectTag } = this.props

    try {
      const response = await ProjectTagsController.getForm({ id: propsProjectTag.id })
      const { project_tag } = response

      this.setState({
        projectTag: {
          ...project_tag,
          ...propsProjectTag
        },
        didInitialLoad: true,
      })
    } catch (ex) {
      console.error(ex)
    }
  }

  onNameChange(e) {
    const { projectTag } = this.state;
    const name = e.currentTarget.value;

    this.setState({
      projectTag: {
        ...projectTag,
        name: name
      },
    })
  }

  async onFormSubmit(e) {
    e.preventDefault();
    const { projectTag } = this.state;
    const { close, onSubmit, t } = this.props

    try {

      if (projectTag.id) { // Do update
        const response = await ProjectTagsController.update(projectTag)
        const { errors } = response;

        if (errors) {
          this.setState({
            errors: errors
          });
          Notification.notifyError(t('ProjectTagModal::Oops something went wrong'))
        }
        else {
          Notification.notifySuccess(t('ProjectTagModal::Project tag successfully updated.'))
          if (onSubmit) onSubmit(response)
          close()
        }
      }
      else {
        const response = await ProjectTagsController.create(projectTag)
        const { errors } = response;

        if (errors) {
          this.setState({
            errors: errors
          });
          Notification.notifyError(t('ProjectTagModal::Oops something went wrong'))
        }
        else {
          Notification.notifySuccess(t('ProjectTagModal::Project tag successfully created.'))
          if (onSubmit) onSubmit(response)
          close()
        }
      }
    } catch (ex) {
      console.error(ex)
    }
  }

  onProjectTagModalCloseClick() {
    this.props.close()
  }

  onErrorsDismiss() {
    this.setState({
      errors: {}
    })
  }

  renderDetails() {
    const { t, currentUser } = this.props
    const { activeTab, projectTag } = this.state;
    const { workspace: { setting } } = currentUser

    if (activeTab !== ProjectTagModalTab.DETAILS) return null;

    return (
      <div data-tab='details'>
        <div className='grid'>
          <div className='grid-cell with-6col'>
            <div className='form-item'>
              <label>
                {t('ProjectTagModal::Name')}
                <span> *</span>
              </label>
              <input
                type='text'
                name='name'
                onChange={this.onNameChange}
                value={projectTag.name}
                placeholder={t('ProjectTagModal::Name')}
                required
              />
            </div>
          </div>
        </div>
      </div>
    )
  }

  render() {
    const { projectTag, t } = this.props
    const { activeTab, didInitialLoad, errors } = this.state

    return (
      <ModalWindow>
        <ModalHeader
          title={projectTag?.id ? t('ProjectTagModal::Edit project tag') : t('ProjectTagModal::Create project tag')}
          onCloseClick={this.onProjectTagModalCloseClick}
        />

        {!didInitialLoad && <ModalLoader />}
        {didInitialLoad && <ModalMiddle>
          <ModalContent>
            <form onSubmit={this.onFormSubmit}>
              {this.renderDetails()}
              <input type='submit' style={{ display: 'none' }} onClick={this.onFormSubmit} />
            </form>
          </ModalContent>

          <div className='modal-footer'>
            <div />
            <div className='modal-footer-actions'>
              <div key='main-action' className='popover-wrapper'>
                <TooltipError
                  errors={errors}
                  onDismiss={this.onErrorsDismiss}
                />
                <a href='javascript://' className='button button-success' onClick={this.onFormSubmit}>
                  {t('ProjectTagModal::Save')}
                </a>
              </div>
            </div>
          </div>
        </ModalMiddle>}
      </ModalWindow>
    )
  }
}

const mapStateToProps = (state: AppState): IStateToProps => {
  const {
    authentication: {
      currentUser,
    },
    modals: {
      projectTagModal: {
        projectTag,
        onSubmit,
      }
    }
  } = state

  return {
    currentUser: currentUser,
    projectTag: projectTag,
    onSubmit: onSubmit,
  }
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): IDispatchToProps => {
  return {
    close: () => dispatch(closeProjectTagModal()),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(ProjectTagModal))